import { useEffect, useState, type FormEvent } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { authAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import { extractErrorMessage } from '@/hooks/useErrorHandler';
import { validatePasswordStrength } from '@/lib/passwordRules';
import { PasswordStrengthChecklist } from '@/components/auth/PasswordStrengthChecklist';
import './ChangePasswordModal.scss';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Change-password modal.
 *
 * Launched from the topbar's user menu. Self-contained state: current
 * password / new password / confirm, plus the strength checklist
 * mirroring `PasswordMixin` on the BE.
 *
 * Why a modal (not a separate page or an embedded card):
 *   - It's a one-off action — operator doesn't navigate to it, they
 *     trigger it. Keeping it overlay-style preserves their place.
 *   - Doesn't tangle with the Profile page's save state.
 *
 * On open we reset to a clean state — a previous failed attempt
 * shouldn't repopulate the field on the next open.
 */
export const ChangePasswordModal = ({ open, onClose }: Props) => {
  const toast = useToast();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset every time the modal opens. Avoids the awkward case where a
  // user opens, types, closes without saving, then reopens and sees
  // their old draft.
  useEffect(() => {
    if (open) {
      setOldPassword('');
      setNewPassword('');
      setConfirm('');
      setError(null);
    }
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);

    if (!oldPassword) {
      setError('Enter your current password.');
      return;
    }
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === oldPassword) {
      setError('New password must be different from the current one.');
      return;
    }
    const strength = validatePasswordStrength(newPassword);
    if (strength) {
      setError(strength);
      return;
    }

    setSaving(true);
    try {
      await authAPI.changePassword(oldPassword, newPassword);
      toast.success('Password changed.');
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err) || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  const ready =
    oldPassword.length > 0 &&
    newPassword.length > 0 &&
    confirm.length > 0 &&
    !saving;

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title="Change password"
      size="md"
      closeOnBackdrop={!saving}
      footer={
        <div className="change-pw-modal__footer">
          <span className="change-pw-modal__trust" aria-hidden="true">
            <ShieldCheck size={12} />
            Server re-validates · Current password required
          </span>
          <div className="change-pw-modal__footer-actions">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="change-pw-modal-form"
              variant="primary"
              size="md"
              leftIcon={<KeyRound size={14} />}
              disabled={!ready}
              loading={saving}
            >
              {saving ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </div>
      }
    >
      {/* The submit button lives in the modal footer; this `id` lets it
          submit this form via the `form="…"` attribute even though the
          button is outside the <form> element. */}
      <form
        id="change-pw-modal-form"
        className="change-pw-modal"
        onSubmit={onSubmit}
      >
        <p className="change-pw-modal__intro">
          Sessions on other devices remain active until they expire.
        </p>

        <div className="change-pw-modal__fields">
          <label className="change-pw-modal__field">
            <span className="change-pw-modal__field-label">Current password</span>
            <input
              type="password"
              className="change-pw-modal__input"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              autoFocus
            />
          </label>
          <label className="change-pw-modal__field">
            <span className="change-pw-modal__field-label">New password</span>
            <input
              type="password"
              className="change-pw-modal__input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </label>
          <label className="change-pw-modal__field">
            <span className="change-pw-modal__field-label">Confirm new password</span>
            <input
              type="password"
              className="change-pw-modal__input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </label>
        </div>

        <PasswordStrengthChecklist password={newPassword} surface="light" />

        {error && (
          <div className="change-pw-modal__error" role="alert">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
};
